// React & Hooks

import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// External Library and Internalization
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useDetectorManagement from '@/domains/asset-management/hooks/useDetectorManagement';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
// UI Component
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
// Custom Hooks
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useUnmappedSiteRoad from '@/shared/hooks/useUnmappedSiteRoad';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

import 'air-datepicker/air-datepicker.css';
import { useQuery } from '@tanstack/react-query';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
// Utilities
// import { formatDateToYYYYMMDD } from "@/utils/date";
import dayjs from 'dayjs';
import { number } from 'mathjs';
import { fetchBoxList } from '@/domains/asset-management/apis/box-management';
import type { BoxListResponse } from '@/domains/asset-management/types/box';
import type {
  CreateDetectorPayload,
  DetectorDetailData,
  DetectorFormValues,
  UpdateDetectorPayload,
} from '@/domains/asset-management/types/detector';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// detector endpoint allows: id (detector_id) | updated_time.
const DETECTOR_SORT_FIELD_MAP: Record<string, string> = {
  detector_id: 'id',
  updated_time: 'updated_time',
};

const DetectorManagement = () => {
  const { t, i18n } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const isNewClickedRef = useRef(isNewClicked);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null | string>(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  //Params
  const [selectedOption, setSelectedOption] = useState<string>('');
  const table = useServerTable<{ input?: string; site_type?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;
  const detectorColumns = useMemo(
    () => [
      {
        title: 'No',
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      // TODO: i18n - hardcoded Korean string
      {
        title: '검지기 ID',
        field: 'detector_id',
        widthGrow: 0.7,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: '명칭',
        field: 'name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '시리얼 넘버',
        field: 's_n',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '매핑 사이트 타입',
        field: 'site_type_value',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '매핑 사이트',
        field: 'site_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '매핑 접근로',
        field: 'road_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size]
  );
  const [optionParams] = useState('upper-code=102');
  const [resource, setResource] = useState('resource=detector');
  const [selectedDetector, setSelectedDetector] = useState({
    dt_id: null,
  });
  const selectedDetectorRef = useRef(selectedDetector);

  const detailDetector = useCallback((data: DetectorDetailData) => {
    return {
      detector_id: data.detector_id || null,
      site_id: data.site_id || 'NO_MAPPING',
      road_id: data.road_id || 'NO_MAPPING',
      remote_terminal_unit_id: data.remote_terminal_unit_id || null,
      name: data.name || null,
      description: data.description || null,
      installed_date: data.installed_date || null,
      pic_name: data.pic_name || null,
      pic_phone_number: data.pic_phone_number || null,
      model: data.model || null,
      s_n: data.s_n || null,
      lat: data.lat || null,
      lng: data.lng || null,
      manufacturer: data.manufacturer || null,
      ip: data.ip || null,
      mac: data.mac || null,
      camera_url: data.camera_url || null,
      config_json: data.config_json || null,
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));

      if (name === 'site_id') {
        setSelectedSiteId(value);
      }

      if (isNewClickedRef.current) {
        setHasChangesCreate(true);
      } else {
        setHasChangesUpdate(true);
      }

      if (selectedDetectorRef.current?.dt_id) {
        setHasChangesUpdate(true);
      }
    },
    []
  );

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const optionsDate: AirDatepickerOptions<HTMLElement> = {
      autoClose: true,
      locale: locale,
      position: 'top center' as AirDatepickerPosition,
      onSelect: (date) => {
        const selected = Array.isArray(date.formattedDate)
          ? date.formattedDate[0]
          : date.formattedDate;
        handleInputChange({
          target: {
            name: 'installed_date',
            value: dayjs(selected).format('YYYY-MM-DD'),
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        setFormValues((prevValues) => ({
          ...prevValues,
          installed_date: dayjs(selected).format('YYYY-MM-DD'),
        }));
      },
    };

    const datepicker = new AirDatepicker('[name="installed_date"]', optionsDate);

    return () => {
      datepicker.destroy();
    };
  }, [i18n.language, handleInputChange]);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedDetectorRef.current = selectedDetector;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedDetector]);

  //Values
  const [formValues, setFormValues] = useState<DetectorFormValues>({
    detector_id: null,
    site_id: null,
    road_id: null,
    remote_terminal_unit_id: null,
    name: null,
    description: null,
    installed_date: null,
    pic_name: null,
    pic_phone_number: null,
    model: null,
    s_n: null,
    lat: null,
    lng: null,
    manufacturer: null,
    camera_url: null,
    ip: null,
    mac: null,
    config_json: null,
  });

  const emptyDetail = () => {
    setFormValues((prevValues) => {
      const emptyValues = {} as DetectorFormValues;

      (Object.keys(prevValues) as (keyof DetectorFormValues)[]).forEach((key) => {
        emptyValues[key] = null;
      });
      return emptyValues;
    });
  };

  //Button
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedSiteId(null);
    setSelectedDetector({ dt_id: null });
    setNewId(null);
  };

  const {
    detectorListData,
    detailDetectorData,
    deleteDetector,
    createDetector,
    updateDetector,
    refetchDetailDetector,
  } = useDetectorManagement({
    dtID: selectedDetector?.dt_id,
    queryParams: queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: async () => {
      refetchDetailDetector();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      const newID = responseData.detector_id.detector_id;
      setNewId(newID);
    },
  });

  const pageData = detectorListData?.data;
  const data = pageData?.rows ?? [];
  const dataDetector = detailDetectorData?.data;

  const { commonListData } = useCommonCodes({ optionParams });
  const { unmappedSiteRoad } = useUnmappedSiteRoad({ resource });
  // size=0 → full box list (this is a mapping dropdown source, not a paged table).
  const { data: boxListData } = useQuery<BoxListResponse>({
    queryKey: ['boxListData', 'size=0'],
    queryFn: () => fetchBoxList('size=0'),
    staleTime: 1000 * 60 * 5,
  });

  const dataSiteRoad = unmappedSiteRoad?.data;

  useEffect(() => {
    if (selectedSiteId === 'NO_MAPPING') {
      setFormValues((prevValues) => ({
        ...prevValues,
        road_id: 'NO_MAPPING',
      }));
    }
  }, [selectedSiteId]);

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedOption(value);
  }, []);

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    index: 'detector_id',
    resizableRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    selectableRowsCheck: (row: RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({
        input: inputVal || undefined,
        site_type: selectedOption || undefined,
      });
    },
    [selectedOption, table]
  );

  const handleReset = () => {
    setSelectedOption('');
    table.reset();
  };

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message
          .confirmClicked()
          .then(() => {
            tbRef.current?.deselectRow();
            row.select();
            row.getElement().classList.add('custom-hover');

            const rowData = row.getData();
            setSelectedDetector({
              dt_id: rowData.detector_id,
            });
            setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
            setResource(`id=${rowData.detector_id}&resource=detector`);
            setDisabledForm(false);
            setHasChangesUpdate(false);
            setHasChangesCreate(false);
            setIsNewClicked(false);
          })
          .catch(() => {
            row.deselect();

            const prevId = selectedDetectorRef.current?.dt_id;
            if (prevId) {
              const prevRow = tbRef.current?.getRow(prevId) || null;
              prevRow?.select();
              prevRow?.getElement().classList.add('custom-hover');
            }
          });
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedDetector({
          dt_id: rowData.detector_id,
        });
        setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
        setResource(`id=${rowData.detector_id}&resource=detector`);
        setDisabledForm(false);
        enableUPDATEButtons();
        setIsNewClicked(false);
      }
      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true);
        enableRegisterButtons();
        setSelectedDetector({ dt_id: null });
        setResource(`resource=detector`);
        setSelectedSiteId(null);
        setDisabledForm(false);
        setHasChangesUpdate(false);
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedDetector({ dt_id: null });
      setResource(`resource=detector`);
      setSelectedSiteId(null);
      setDisabledForm(false);
      setHasChangesUpdate(false);
    }
    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        enableInitialButtons();
        setHasChangesUpdate(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedSiteId(null);
        setSelectedDetector({ dt_id: null });
        // ----------------------------------------------------------

        requestAnimationFrame(() => {
          if (nameRef.current) {
            nameRef.current.focus();
          }
        });
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
          setSelectedSiteId(null);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedSiteId(null);
      }
    } else {
      reloadCallback();
    }
  };

  const handleRegistButtonClick = () => {
    const {
      site_id,
      detector_id: _detector_id,
      lat,
      lng,
      description,
      road_id,
      remote_terminal_unit_id,
      config_json,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );

    if (isEmptyField || lat == null || lng == null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    const payload: CreateDetectorPayload = {
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : number(site_id),
      road_id: !road_id || road_id === 'NO_MAPPING' ? null : number(road_id),
      remote_terminal_unit_id: remote_terminal_unit_id ? Number(remote_terminal_unit_id) : null,
      name: formValues.name ?? '',
      lat,
      lng,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      manufacturer: formValues.manufacturer ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      ip: formValues.ip ?? '',
      mac: formValues.mac ?? '',
      camera_url: formValues.camera_url ?? '',
      description: description || undefined,
      config_json: config_json || undefined,
    };

    createDetector(payload);
  };

  const handleConfirmButtonClick = async () => {
    const {
      lat,
      lng,
      detector_id,
      description,
      road_id,
      site_id,
      remote_terminal_unit_id,
      config_json,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );

    if (isEmptyField || lat == null || lng == null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    // Make a payload
    const payload: UpdateDetectorPayload = {
      detector_id: Number(detector_id),
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : number(site_id),
      road_id: !road_id || road_id === 'NO_MAPPING' ? null : number(road_id),
      remote_terminal_unit_id: remote_terminal_unit_id ? Number(remote_terminal_unit_id) : null,
      name: formValues.name ?? '',
      lat,
      lng,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      manufacturer: formValues.manufacturer ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      ip: formValues.ip ?? '',
      mac: formValues.mac ?? '',
      camera_url: formValues.camera_url ?? '',
      description: description || undefined,
      config_json: config_json || undefined,
    };

    updateDetector({
      detectorData: payload,
      params: {
        detector_id: payload.detector_id,
      },
    });
  };

  const handleDeleteButtonClick = () => {
    if (dataDetector?.site_id || dataDetector?.road_id) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('현재 검지기에 매핑된 사이트가 존재합니다. 먼저 매핑을 해제해주세요');
      return;
    }
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });

    message.confirmClicked().then(() => {
      deleteDetector(selectedDetector?.dt_id ?? '');
      if (refetchDetailDetector) {
        refetchDetailDetector();
      }
    });
  };

  const roadOptions = selectedSiteId
    ? [
        // TODO: i18n - hardcoded Korean string
        { value: 'NO_MAPPING', label: '매핑 없음' },
        ...(dataSiteRoad?.sites
          ?.find((site) => site.site_id === Number(selectedSiteId))
          ?.roads.map((road) => ({
            value: String(road.road_id),
            label: `${road.name} (${road.road_id})`,
          })) || []),
      ]
    : [];

  useEffect(() => {
    if (dataDetector) {
      const data = detailDetector(dataDetector);
      setFormValues(data);
    }
  }, [dataDetector, detailDetector]);

  const logData = dataDetector
    ? [
        { label: t('cmn > registered by'), value: dataDetector.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataDetector.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataDetector.registered_time))
            : dataDetector.registered_time,
        },
        { label: t('cmn > updated by'), value: dataDetector.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataDetector.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataDetector.updated_time))
            : dataDetector.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        {/* TODO: i18n - hardcoded Korean string */}
        <h3>센서 관리</h3>
        <h3>&gt;</h3>
        <h3>검지기 속성 관리</h3>
      </div>

      <ContainerCard>
        {/* TODO: i18n - hardcoded Korean string */}
        <Filtering
          searchRef={searchRef}
          labelSelect="매핑 사이트 타입"
          placeholder="명칭 / 시리얼 넘버"
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={selectedOption}
            onChange={handleOnChangeInputSelect}
            options={
              commonListData?.['102']
                ? [
                    { value: '', label: t('cmn > all') },
                    ...(commonListData['102']?.code?.map((code, index) => ({
                      value: code ?? '',
                      label: commonListData['102']?.name[index] ?? '',
                    })) || []),
                  ]
                : []
            }
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={data}
            id="detector-management-table"
            columns={detectorColumns}
            layout={'fitColumns'}
            className="tabulator-custom w-full "
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            options={optionsTabulator}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              // Header click → delegate sorting to the server (see useServerTable).
              dataSorted: remoteSortHandler(table, DETECTOR_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current) return;
                if (selectedDetector?.dt_id) {
                  try {
                    const row = tbRef.current.getRow(selectedDetector?.dt_id) || null;
                    if (row && typeof row.select === 'function') {
                      row.select();
                      row.getElement().classList.add('custom-hover');
                    }
                  } catch {
                    /* row not found */
                  }
                } else if (newId) {
                  try {
                    const row = tbRef.current.getRow(newId) || null;
                    if (row && typeof row.select === 'function') {
                      tbRef.current.scrollToRow(row, 'bottom', true);
                      row.select();
                      row.getElement().classList.add('custom-hover');
                      handleRowSelected(row);
                    }
                  } catch {
                    /* row not found */
                  }
                }
              },
            }}
          />

          {/* Server-side Pagination Controls - Tabulator style */}
          <div className="tabulator-footer">
            <div className="tabulator-paginator">
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(1)}
                disabled={table.page === 1}
              >
                {t('cmn > first page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page - 1)}
                disabled={table.page === 1}
              >
                {t('cmn > page before')}
              </button>
              <span className="tabulator-pages">
                {Array.from({ length: Math.min(5, pageData?.total_pages ?? 0) }, (_, i) => {
                  const totalPages = pageData?.total_pages ?? 0;
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (table.page <= 3) {
                    pageNum = i + 1;
                  } else if (table.page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = table.page - 2 + i;
                  }
                  return (
                    <button
                      type="button"
                      key={pageNum}
                      className={`tabulator-page ${table.page === pageNum ? 'active' : ''}`}
                      onClick={() => table.setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page + 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > next page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(pageData?.total_pages ?? 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > last page')}
              </button>
            </div>
            <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
              총 {pageData?.total ?? 0} 건
            </div>
          </div>
        </div>
      </ContainerCard>

      <ContainerCard className=" flex flex-col">
        <div className="box-management-col flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            {/* TODO: i18n - hardcoded Korean string */}
            <DetailForm
              className="items-center!"
              label="검지기 ID"
              value={formValues.detector_id || ''}
              name={'detector_id'}
              disabled={true}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="명칭"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name={'name'}
              maxLength={100}
              formRef={nameRef}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="설치 위/경도"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2">
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lat || ''}
                    name={'lat'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />

                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lng || ''}
                    name={'lng'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              className="items-center!"
              label="설치 일시"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.installed_date || ''}
              name={'installed_date'}
              readonly={true}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 사이트"
              optionSelect={[
                { value: 'NO_MAPPING', label: '매핑 없음' },
                ...(dataSiteRoad?.sites?.map((site) => ({
                  value: site.site_id.toString(),
                  label: `${site.name} (${site.site_id})`,
                })) || []),
              ]}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.site_id || ''}
              name={'site_id'}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 접근로"
              optionSelect={roadOptions}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.road_id || undefined}
              name={'road_id'}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 함체"
              optionSelect={[
                ...(boxListData?.data?.rows?.map((box) => ({
                  value: String(box.remote_terminal_unit_id),
                  label: `${box.name} (${box.remote_terminal_unit_id})`,
                })) || []),
              ]}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.remote_terminal_unit_id || ''}
              name={'remote_terminal_unit_id'}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="담당자"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.pic_name || ''}
              name={'pic_name'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="담당자 전화번호"
              required={true}
              value={formValues.pic_phone_number || ''}
              name={'pic_phone_number'}
              disabled={disabledForm}
              onChange={handleInputChange}
              maxLength={100}
              pattern={'^[0-9]*$'}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="모델명"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.model || ''}
              name={'model'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="시리얼 넘버"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.s_n || ''}
              name={'s_n'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="제조사"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.manufacturer || ''}
              name={'manufacturer'}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="Mac Address"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.mac || ''}
              name={'mac'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="IP"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.ip || ''}
              name={'ip'}
              maxLength={100}
              pattern={'^[0-9.]*$'}
            />

            <DetailForm
              className="items-center!"
              label="카메라 접속 URL"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.camera_url || ''}
              name={'camera_url'}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[90px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="설명"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.description || ''}
              name={'description'}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[180px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="json 속성 (dev)"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.config_json || ''}
              name="config_json"
              maxLength={10000}
            />
          </div>

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default DetectorManagement;
